import { useMemo, useRef, type MouseEvent as ReactMouseEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, ChevronRight, Sparkles } from 'lucide-react';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { ROUTES } from '@shared/constants/routes';
import type { AudienceId } from '@shared/constants/audiences';
import { useStoredAudience } from '../../hooks/useActiveAudience';
import {
  getRelevantToolsForAudience,
  getToolHref,
  isToolRecommendedFor,
  type ToolDefinition,
  type ToolTone,
} from '@shared/constants/tools-catalog';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

/* ────────────────────────────────────────────────────────
   Tones (mappés sur les clés Tailwind du design system)
──────────────────────────────────────────────────────── */
const TONE: Record<
  ToolTone,
  {
    iconBg: string;
    iconText: string;
    halo: string;
    arrowHover: string;
    ctaBg: string;
    metric: string;
    line: string;
    recommended: string;
  }
> = {
  accent: {
    iconBg: 'bg-accent-50',
    iconText: 'text-accent-700',
    halo: 'from-accent-300/40 via-accent-200/0 to-accent-300/0',
    arrowHover: 'group-hover:bg-accent-500 group-hover:text-ink',
    ctaBg: 'bg-accent-500 hover:bg-accent-400 text-ink',
    metric: 'text-accent-700',
    line: 'bg-accent-500',
    recommended: 'ring-accent-300/60 bg-accent-50/30',
  },
  brand: {
    iconBg: 'bg-brand-50',
    iconText: 'text-brand-700',
    halo: 'from-brand-300/40 via-brand-200/0 to-brand-300/0',
    arrowHover: 'group-hover:bg-brand-600 group-hover:text-white',
    ctaBg: 'bg-brand-600 hover:bg-brand-700 text-white',
    metric: 'text-brand-700',
    line: 'bg-brand-500',
    recommended: 'ring-brand-300/60 bg-brand-50/30',
  },
  lavender: {
    iconBg: 'bg-lavender-50',
    iconText: 'text-lavender-700',
    halo: 'from-lavender-300/40 via-lavender-200/0 to-lavender-300/0',
    arrowHover: 'group-hover:bg-lavender-600 group-hover:text-white',
    ctaBg: 'bg-lavender-600 hover:bg-lavender-700 text-white',
    metric: 'text-lavender-700',
    line: 'bg-lavender-500',
    recommended: 'ring-lavender-300/60 bg-lavender-50/30',
  },
  sage: {
    iconBg: 'bg-sage-100',
    iconText: 'text-sage-500',
    halo: 'from-sage-200/40 via-sage-100/0 to-sage-200/0',
    arrowHover: 'group-hover:bg-ink group-hover:text-ivory-50',
    ctaBg: 'bg-ink hover:bg-stone-800 text-white',
    metric: 'text-sage-500',
    line: 'bg-sage-500',
    recommended: 'ring-stone-300/60 bg-stone-50/30',
  },
} as const;

/* Limite de cartes affichées sur la home : on ne sature pas l'écran. */
const MAX_TOOLS_ON_HOME = 5;

/* ────────────────────────────────────────────────────────
   Section
──────────────────────────────────────────────────────── */
export function ToolsBento() {
  const audience = useStoredAudience();
  const audienceId = audience?.id ?? null;

  // Outils visibles, triés par pertinence pour l'audience (recommandés en tête).
  const tools = useMemo(
    () => getRelevantToolsForAudience(audienceId).slice(0, MAX_TOOLS_ON_HOME),
    [audienceId],
  );

  // Première carte affichée en "wide" si on a 5 outils (sinon grille standard).
  const layoutTools = useMemo(() => {
    return tools.map((tool, idx) => ({
      tool,
      wide: tools.length >= 5 && idx === 0,
    }));
  }, [tools]);

  const recommendedCount = useMemo(
    () => tools.filter((t) => isToolRecommendedFor(t.id, audienceId)).length,
    [tools, audienceId],
  );

  return (
    <section
      aria-labelledby="tools-bento-title"
      className="relative overflow-hidden bg-paper py-16 md:py-24"
    >
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 dot-grid-masked opacity-50" />
      </div>

      <Container className="relative">
        <Reveal>
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur">
              <span className="live-dot" aria-hidden />
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                Outils interactifs
              </span>
            </div>
            <h2
              id="tools-bento-title"
              className="mt-5 font-display text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold tracking-[-0.025em] text-balance text-ink"
            >
              {audienceId && audience ? (
                <>
                  Vos outils{' '}
                  <span className="text-gradient-mono">{audience.shortLabel.toLowerCase()}</span>,
                  chiffrés en quelques clics.
                </>
              ) : (
                <>
                  Estimez, comparez, décidez —{' '}
                  <span className="text-gradient-mono">en accès libre, sans passer par un commercial</span>.
                </>
              )}
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-stone-600">
              {audienceId ? (
                <>
                  Outils filtrés et priorisés pour votre profil. Accès libre, sans inscription,
                  chiffres sourcés sur les barèmes officiels en vigueur.
                </>
              ) : (
                <>
                  Estimateurs d’aides, comparateurs d’offres, audit factures. Tous en accès libre,
                  sans inscription, sans relance commerciale.
                </>
              )}
            </p>
          </div>
        </Reveal>

        {/* Bandeau personnalisation Linear-style */}
        <AnimatePresence>
          {audienceId && recommendedCount > 0 && (
            <motion.div
              key={audienceId}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: easeStripe }}
              className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-brand-200/50 bg-brand-50/40 px-4 py-3 shadow-hairline"
            >
              <div className="flex items-center gap-2.5">
                <span className="live-dot" aria-hidden />
                <span className="text-[13px] font-medium text-brand-800">
                  Affichage personnalisé pour le profil{' '}
                  <strong className="font-bold">{audience!.label}</strong>
                </span>
              </div>
              <a
                href={ROUTES.outils}
                className="group/lnk flex shrink-0 items-center gap-1 font-mono text-[11px] font-semibold text-brand-700 transition-colors hover:text-brand-800"
              >
                Voir tous les outils
                <ChevronRight className="h-3 w-3 transition-transform duration-200 ease-out group-hover/lnk:translate-x-0.5" strokeWidth={2.5} />
              </a>
            </motion.div>
          )}
        </AnimatePresence>

        {tools.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/40 py-12 text-center">
            <p className="text-[14px] text-stone-500">
              Pas encore d’outil dédié à ce profil — mais on peut chiffrer votre projet manuellement.
            </p>
            <a
              href={ROUTES.contact}
              className="group/lnk mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-brand-700 hover:text-brand-800"
            >
              Parler à un conseiller
              <ChevronRight className="h-3 w-3 transition-transform duration-200 ease-out group-hover/lnk:translate-x-0.5" strokeWidth={2.5} />
            </a>
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-3 lg:[grid-auto-rows:1fr]">
            {layoutTools.map(({ tool, wide }, idx) => (
              <ToolCardItem
                key={tool.id}
                tool={tool}
                index={idx}
                recommended={isToolRecommendedFor(tool.id, audienceId)}
                wide={wide}
                audienceId={audienceId}
              />
            ))}
          </div>
        )}
      </Container>
    </section>
  );
}

/* ────────────────────────────────────────────────────────
   Card
──────────────────────────────────────────────────────── */
interface ToolCardItemProps {
  tool: ToolDefinition;
  index: number;
  recommended: boolean;
  wide: boolean;
  audienceId: AudienceId | null;
}

function ToolCardItem({ tool, index, recommended, wide, audienceId }: ToolCardItemProps) {
  const ref = useRef<HTMLAnchorElement>(null);
  const Icon = getIcon(tool.iconName);
  const t = TONE[tool.tone];
  const href = getToolHref(tool.id, audienceId);

  const onMove = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    el.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <motion.a
      ref={ref}
      href={href}
      onMouseMove={onMove}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay: index * 0.06, ease: easeStripe }}
      whileHover={{ y: -3 }}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-2xl border bg-paper p-5 shadow-card transition-all duration-200 ease-out hover:shadow-card-hover md:p-6',
        wide && 'lg:col-span-2',
        recommended
          ? cn('border-2', t.recommended)
          : 'border-stone-200 hover:border-stone-300',
      )}
    >
      {/* Spotlight cursor-follow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100"
        style={{
          background:
            'radial-gradient(360px circle at var(--mx, 50%) var(--my, 50%), rgba(16,185,129,0.10), transparent 60%)',
        }}
      />
      {/* Halo */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100',
          t.halo,
        )}
      />
      {/* Top line */}
      <div
        aria-hidden
        className={cn(
          'absolute inset-x-6 top-0 h-px origin-left transition-transform duration-300 ease-out',
          t.line,
          recommended ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100',
        )}
      />

      {/* Header: icon + badges */}
      <div className="relative flex items-start justify-between gap-3">
        <span
          className={cn(
            'inline-flex h-10 w-10 items-center justify-center rounded-lg ring-1 ring-inset transition-transform duration-200 ease-out group-hover:scale-105',
            t.iconBg,
            t.iconText,
            'ring-stone-200/60',
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={1.7} />
        </span>
        <div className="flex flex-wrap gap-1.5">
          {recommended && (
            <span className="inline-flex items-center gap-1 rounded-md border border-brand-200/60 bg-brand-50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-brand-700">
              <Sparkles className="h-2.5 w-2.5" strokeWidth={2.2} />
              Recommandé
            </span>
          )}
          {tool.badge && !recommended && (
            <span className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-stone-50 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-stone-700">
              <Sparkles className="h-2.5 w-2.5" strokeWidth={2.2} />
              {tool.badge}
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <p className="relative mt-5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-400">
        {tool.eyebrow}
      </p>
      <h3
        className={cn(
          'relative mt-1.5 font-display font-semibold leading-tight text-ink',
          wide ? 'text-xl md:text-2xl' : 'text-[17px]',
        )}
      >
        {tool.title}
      </h3>
      <p
        className={cn(
          'relative mt-2 leading-relaxed text-stone-600',
          wide ? 'text-[14px] md:text-[15px]' : 'text-[13px]',
        )}
      >
        {tool.description}
      </p>

      {/* Bullets */}
      <ul className="relative mt-4 flex flex-wrap gap-1.5">
        {tool.bullets.map((b) => (
          <li
            key={b}
            className="inline-flex items-center gap-1.5 rounded-md border border-stone-100 bg-stone-50/60 px-2 py-0.5 font-mono text-xs font-medium text-stone-600"
          >
            <span className={cn('h-1 w-1 rounded-sm', t.line)} />
            {b}
          </li>
        ))}
      </ul>

      {/* Footer */}
      <div className="relative mt-auto flex items-end justify-between gap-3 border-t border-stone-100 pt-4">
        <div>
          <p className={cn('font-display text-2xl font-bold leading-none tracking-[-0.025em] tabular-nums', t.metric)}>
            {tool.metric.value}
          </p>
          <p className="mt-1 font-mono text-xs text-stone-500">{tool.metric.label}</p>
        </div>

        {tool.cta ? (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-semibold transition-all duration-200 ease-out',
              t.ctaBg,
            )}
          >
            {tool.cta}
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
        ) : (
          <span
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md bg-stone-100 text-stone-600 transition-all duration-200 ease-out',
              t.arrowHover,
            )}
          >
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
        )}
      </div>
    </motion.a>
  );
}
